import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Save, Printer, Archive, RotateCcw, Trash2, ChevronDown, ChevronUp, Plus, Download } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];
const DAYS_SHORT = ["Mon", "Tue", "Wed", "Thu", "Fri"];

const MONTHS = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December"
];

// ── Template colour themes ─────────────────────────────────────────────────
export const THEMES = {
  infant:  { label: "Infant",   bg: "bg-sky-50",    border: "border-sky-200",   header: "bg-sky-600",    badge: "bg-sky-100 text-sky-800"    },
  toddler: { label: "Toddler",  bg: "bg-pink-50",   border: "border-pink-200",  header: "bg-pink-600",   badge: "bg-pink-100 text-pink-800"  },
  prek:    { label: "Pre-K",    bg: "bg-violet-50", border: "border-violet-200",header: "bg-violet-600", badge: "bg-violet-100 text-violet-800"},
  summer:  { label: "Summer",   bg: "bg-green-50",  border: "border-green-200", header: "bg-green-600",  badge: "bg-green-100 text-green-800" },
};

// ── Default row definitions per template type ─────────────────────────────
const DEFAULT_ROWS = {
  infant: [
    { key: "theme",     label: "Theme for the Week" },
    { key: "books",     label: "Book of the Week" },
    { key: "r845",      label: "8:45–9:00 AM — Breakfast" },
    { key: "r915",      label: "9:15–9:30 AM — Potty Run / Morning Prep" },
    { key: "r945",      label: "9:45–10:00 AM — Circle Time (Greeting, Calendar & Weather, Concept Review: Numbers, Shapes, Colors, Names)" },
    { key: "r_dvd",     label: "Circle Time DVD / CD" },
    { key: "r1000",     label: "10:00 AM — Art (at least 5 projects per week)" },
    { key: "r1030",     label: "10:00–10:30 AM — Extra Snack / Outside Play" },
    { key: "r_potty2",  label: "10:30–10:45 AM — Potty Run" },
    { key: "r1130",     label: "11:30 AM — Lunch" },
    { key: "r_potty3",  label: "12:00 PM — Potty Run" },
    { key: "r_nap",     label: "12:00–2:30 PM — Nap Time (downstairs by 12:15)" },
    { key: "r230",      label: "2:30 PM — PM Snack" },
    { key: "r_pm",      label: "2:30–5:00 PM — PM Activity (Group Blocks / Game / Puzzle / Table Toys)" },
    { key: "r400",      label: "4:00 PM — Detail Maintenance (Movie Time and Table Activity or Floor Blocks until all children dismissed)" },
    { key: "field",     label: "Field Trip Suggestions" },
    { key: "materials", label: "Materials Needed" },
  ],
  toddler: [
    { key: "theme",     label: "Theme for the Week" },
    { key: "books",     label: "Book of the Week" },
    { key: "r845",      label: "8:45–9:00 AM — Breakfast" },
    { key: "r915",      label: "9:15–9:30 AM — Potty Run / Morning Prep" },
    { key: "r945",      label: "9:45–10:00 AM — Circle Time (Greeting, Calendar & Weather, Concept Review: Numbers, Shapes, Colors, Names)" },
    { key: "r_dvd",     label: "Circle Time DVD / CD" },
    { key: "r1000",     label: "10:00 AM — Art (at least 5 projects per week)" },
    { key: "r1030",     label: "10:00–10:30 AM — Extra Snack / Outside Play" },
    { key: "r_potty2",  label: "10:30–10:45 AM — Potty Run" },
    { key: "r1130",     label: "11:30 AM — Lunch" },
    { key: "r_potty3",  label: "12:00 PM — Potty Run" },
    { key: "r_nap",     label: "12:00–2:30 PM — Nap Time (downstairs by 12:15)" },
    { key: "r230",      label: "2:30 PM — PM Snack" },
    { key: "r_pm",      label: "2:30–5:00 PM — PM Activity (Group Blocks / Game / Puzzle / Table Toys)" },
    { key: "r400",      label: "4:00 PM — Detail Maintenance (Movie Time and Table Activity or Floor Blocks until all children dismissed)" },
    { key: "field",     label: "Field Trip Suggestions" },
    { key: "materials", label: "Materials Needed" },
  ],
  prek: [
    { key: "theme",     label: "Theme for the Week" },
    { key: "books",     label: "Book of the Week" },
    { key: "r845",      label: "8:45 AM — Educational TV" },
    { key: "r900",      label: "9:00 AM — Breakfast" },
    { key: "r930",      label: "9:30–10:00 AM — Circle Time (Letter / Shape / Number / Color Review, Days of Week, Name / Age / Birthday, Theme Song / Poem / Fingerplay, Greeting Song, Calendar & Weather, Closing Song)" },
    { key: "r1000",     label: "10:00 AM — Art (at least 5 projects per week)" },
    { key: "r1015s",    label: "10:15 AM — Extra Snack" },
    { key: "r1015c",    label: "10:15 AM — Computer Learning (abcmouse.com, ixl.com, nickjr.com, disneyjr.com)" },
    { key: "r1030",     label: "10:30 AM — Music / Math / Science Discovery / Social Studies" },
    { key: "r1045",     label: "10:45 AM — Free Play / Playground / Gym (weather permitting)" },
    { key: "r1115",     label: "11:15 AM — Organized Activity (Blocks / Writing Practice / PlayDough / Coloring / Dramatic Play / Instruments / Group Game)" },
    { key: "r1145",     label: "11:45 AM–Noon — Lunch" },
    { key: "r_nap",     label: "12:30–2:30 PM — Nap Time / Movie Time" },
    { key: "r230",      label: "2:30 PM — PM Snack" },
    { key: "r245",      label: "2:45–4:00 PM — Free Play" },
    { key: "field",     label: "Field Trip Suggestions" },
    { key: "materials", label: "Materials Needed" },
  ],
  summer: [
    { key: "food_theme", label: "Food Theme" },
    { key: "r845",       label: "8:45 a.m. — Breakfast" },
    { key: "r900",       label: "9:00 a.m. — Games & Computers" },
    { key: "r930",       label: "9:30–10:00 a.m. — Outside Group Morning Exercise (Warm Ups / Sprints — List Group Game)" },
    { key: "r1000",      label: "10:00 a.m. — ART (at least 1 craft project per week — 4 art projects, Friday afternoon)" },
    { key: "r1030",      label: "10:30 a.m. — Cooking Activity (List it here) — 3 Days Small Snack / 1 Day Large Snack (Check Supplies Night Before)" },
    { key: "r1100",      label: "11:00 a.m. — Organized Activity — List Group Game (Charades / Bingo, etc. led by teacher)" },
    { key: "r1130",      label: "11:30 a.m. — Lunch" },
    { key: "r1200",      label: "12:00 P.M. — Playground — List Organized Group Game (Art on Wednesday after Movie)" },
    { key: "r100",       label: "1:00 P.M. — Quiet Time on Games, Computers, Color, Puzzles" },
    { key: "r130",       label: "1:30–2:30 P.M. — List Planned Group Activity" },
    { key: "r245",       label: "2:45 P.M. — Snack" },
    { key: "r300",       label: "3:00 P.M. — Playground" },
    { key: "r400",       label: "4:00–5:30 P.M. — Computers & Playstations (Staff Clean Up Time) — Children Departures" },
    { key: "field",      label: "Field Trip Suggestions" },
    { key: "materials",  label: "Materials Needed" },
  ],
};

// ── Print via new window with self-triggering print dialog ──────────────────
export function openPrintWindow(html) {
  const win = window.open("", "_blank");
  if (!win || win.closed || typeof win.document === "undefined") {
    alert("Please allow popups for this site to enable printing.");
    return;
  }
  win.document.write(html);
  win.document.close();
  setTimeout(() => {
    if (win && !win.closed) {
      win.focus();
      win.print();
    }
  }, 600);
}

export function buildPrintHtml({ orgName, type, teacherName, weekDisplay, themeText, rows, cells }) {
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
  const logoUrl = `${BACKEND_URL}/simple-sign-in-logo.png`;
  const THEME_COLORS = { infant: "#0284c7", toddler: "#db2777", prek: "#7c3aed", summer: "#16a34a" };
  const headerColor = THEME_COLORS[type] || "#1d4ed8";
  const typeLabel = THEMES[type]?.label || "";
  const rowsHtml = rows.map(row => {
    const cellData = cells[row.key] || {};
    const rowLabel = cellData.label || row.label;
    const dayHtml = ["monday","tuesday","wednesday","thursday","friday"]
      .map(d => `<td style="border:1px solid #ccc;padding:6px 8px;vertical-align:top;min-height:36px;font-size:9px">${(cellData[d] || "").replace(/\n/g,"<br/>")}</td>`)
      .join("");
    return `<tr><td style="border:1px solid #ccc;padding:6px 8px;font-size:9px;font-weight:600;background:#f8f8f8;width:22%;vertical-align:top">${rowLabel}</td>${dayHtml}</tr>`;
  }).join("");

  return `<!DOCTYPE html><html><head><title>Lesson Plan — ${orgName}</title>
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    html, body { background: white; color: #111; }
    body { font-family: Arial, Helvetica, sans-serif; font-size: 10px; padding: 0.8in 0.4in 0.4in 0.4in; }
    table { width: 100%; border-collapse: collapse; margin-top: 8px; table-layout: fixed; }
    th { border: 1px solid #ccc; padding: 6px 8px; background: #f0f0f0; font-weight: bold; font-size: 9px; }
    td { border: 1px solid #ccc; padding: 6px 8px; vertical-align: top; font-size: 9px; word-wrap: break-word; overflow-wrap: break-word; }
    tr { page-break-inside: avoid; }
    @page { size: 11in 8.5in landscape; margin: 0.3in; }
    @media print {
      body { padding: 0.8in 0 0 0; font-size: 9px; }
      table { font-size: 8px; margin-top: 6px; }
      th, td { font-size: 8px !important; padding: 4px 6px !important; }
    }
  </style></head><body>
  <div style="display:flex;align-items:center;border-bottom:2px solid ${headerColor};padding-bottom:10px;margin-bottom:10px">
    <img src="${logoUrl}" style="height:36px;width:36px;object-fit:contain;margin-right:16px;flex-shrink:0" />
    <div style="flex:1;text-align:center">
      <div style="font-size:16px;font-weight:bold;color:${headerColor}">${orgName || "Childcare Center"}</div>
      <div style="font-size:10px;color:#555;margin-top:2px">${typeLabel} Lesson Plan</div>
    </div>
    <div style="text-align:right;font-size:10px;color:#444;min-width:160px">
      <div style="font-size:13px;font-weight:bold;color:#222">${teacherName || ""}</div>
      <div style="color:#777;margin-top:2px">Teacher / Staff</div>
    </div>
  </div>
  <div style="display:flex;gap:32px;font-size:10px;color:#444;margin-bottom:10px;padding-bottom:6px;border-bottom:1px solid #eee">
    <span><strong>Teacher:</strong> ${teacherName || "—"}</span>
    <span><strong>Week of:</strong> ${weekDisplay}</span>
    <span><strong>Theme:</strong> ${themeText || "—"}</span>
  </div>
  <table>
    <thead><tr>
      <th style="width:22%;text-align:left">Activity / Time</th>
      <th style="text-align:center">Monday</th>
      <th style="text-align:center">Tuesday</th>
      <th style="text-align:center">Wednesday</th>
      <th style="text-align:center">Thursday</th>
      <th style="text-align:center">Friday</th>
    </tr></thead>
    <tbody>${rowsHtml}</tbody>
  </table>
  <script>/* print triggered by parent window after document is written and closed */</script>
  </body></html>`;
}

function buildEmptyCells(rows) {
  const cells = {};
  rows.forEach(r => {
    cells[r.key] = { label: r.label, monday: "", tuesday: "", wednesday: "", thursday: "", friday: "" };
  });
  return cells;
}

// ── Single editable grid ────────────────────────────────────────────────────
function TemplateGrid({ type, orgName, initialData, onSave, onCancel, onPrint, viewingMonth }) {
  const { user } = useAuth();
  const theme = THEMES[type];
  const defaultRows = DEFAULT_ROWS[type];

  const [weekOf, setWeekOf] = useState(initialData?.weekOf || "");
  const [themeText, setThemeText] = useState(initialData?.theme || "");
  const [cells, setCells] = useState(() => initialData?.cells || buildEmptyCells(defaultRows));
  const [rows, setRows] = useState(() => initialData?.rows || defaultRows.map(r => ({ key: r.key, label: r.label })));

  const updateCell = (rowKey, day, value) => {
    setCells(prev => ({ ...prev, [rowKey]: { ...prev[rowKey], [day]: value } }));
  };

  const updateRowLabel = (rowKey, value) => {
    setRows(prev => prev.map(r => r.key === rowKey ? { ...r, label: value } : r));
    setCells(prev => ({ ...prev, [rowKey]: { ...prev[rowKey], label: value } }));
  };

  const restoreDefaults = () => {
    setRows(defaultRows.map(r => ({ key: r.key, label: r.label })));
    setCells(buildEmptyCells(defaultRows));
    setThemeText("");
    setWeekOf("");
    toast.success("Restored to default");
  };

  const handlePrint = () => {
    // Strategy B: build a clean landscape-formatted print document and open in a popup.
    // This bypasses the live grid's form-input clipping + scroll containers entirely.
    try {
      const html = buildPrintHtml({
        orgName: orgName || "",
        type,
        teacherName: user?.name || "",
        weekDisplay: weekOf
          ? new Date(weekOf + "T00:00:00").toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })
          : "—",
        themeText,
        rows,
        cells,
      });
      openPrintWindow(html);
    } catch (e) {
      // Fall back to native print if popup blocked or rendering fails
      if (onPrint) onPrint();
      else window.print();
    }
  };

  const now = new Date();

  return (
    <div className={`rounded-2xl border-2 ${theme.border} ${theme.bg} overflow-hidden`}>
      {/* Letterhead header */}
      <div className={`${theme.header} text-white px-6 py-3 flex items-center gap-4`}>
        <img src="/simple-sign-in-logo.png" alt="Logo" className="h-8 w-8 rounded-lg bg-white p-0.5 object-contain flex-shrink-0" />
        <div className="flex-1 text-center">
          <h2 className="text-lg font-bold leading-tight">{orgName || "Childcare Center"}</h2>
        </div>
        <div className="text-right text-white/80 text-xs flex-shrink-0">
          <p className="font-semibold text-white text-sm">{theme.label} Lesson Plan</p>
          <p>Teacher: {user?.name || user?.email}</p>
        </div>
      </div>

      {/* Week / Theme bar */}
      <div className="px-6 py-3 bg-white/60 border-b border-white/80 flex flex-wrap gap-4 items-center print:hidden">
        <div className="flex items-center gap-2">
          <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Week of:</label>
          <input
            type="date"
            value={weekOf}
            onChange={e => setWeekOf(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
          />
        </div>
        <div className="flex items-center gap-2 flex-1">
          <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Theme:</label>
          <input
            type="text"
            value={themeText}
            onChange={e => setThemeText(e.target.value)}
            placeholder="Enter weekly theme..."
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm flex-1 focus:outline-none focus:ring-2 focus:ring-blue-400"
          />
        </div>
        <div className="text-xs text-gray-400">{now.toLocaleDateString("en-US", { month: "long", year: "numeric" })}</div>
      </div>

      {/* Grid */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[800px]">
          <thead>
            <tr>
              <th className="px-3 py-2 text-left text-xs font-bold uppercase tracking-wide text-gray-500 bg-white/80 w-56 border-b border-r border-gray-200">
                Activity / Time
              </th>
              {DAYS.map((d, i) => (
                <th key={d} className="px-3 py-2 text-center text-xs font-bold uppercase tracking-wide text-gray-600 bg-white/80 border-b border-r border-gray-200">
                  {DAYS_SHORT[i]}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => (
              <tr key={row.key} className={idx % 2 === 0 ? "bg-white/40" : "bg-white/20"}>
                <td className="px-2 py-1.5 border-r border-b border-gray-200 align-top">
                  <textarea
                    value={cells[row.key]?.label ?? row.label}
                    onChange={e => updateRowLabel(row.key, e.target.value)}
                    rows={2}
                    className="w-full bg-transparent text-xs font-semibold text-gray-700 resize-none focus:outline-none focus:bg-white/80 rounded px-1 leading-snug"
                  />
                </td>
                {["monday","tuesday","wednesday","thursday","friday"].map(day => (
                  <td key={day} className="px-2 py-1.5 border-r border-b border-gray-200 align-top">
                    <textarea
                      value={cells[row.key]?.[day] || ""}
                      onChange={e => updateCell(row.key, day, e.target.value)}
                      rows={3}
                      placeholder="..."
                      className="w-full bg-transparent text-xs text-gray-700 resize-none focus:outline-none focus:bg-white/80 rounded px-1 leading-snug placeholder-gray-300"
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Action bar */}
      <div className="px-6 py-3 bg-white/60 border-t border-gray-200 flex items-center justify-between flex-wrap gap-2 print:hidden">
        <Button variant="outline" size="sm" onClick={onCancel}>Cancel</Button>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={restoreDefaults}>
            <RotateCcw className="w-4 h-4 mr-1" /> Restore Default
          </Button>
          <Button variant="outline" size="sm" onClick={handlePrint}>
            <Printer className="w-4 h-4 mr-1" /> Print
          </Button>
          <Button
            size="sm"
            onClick={() => onSave({ weekOf, theme: themeText, cells, rows })}
            className={`${theme.header} text-white hover:opacity-90`}
            data-testid="save-plan-btn"
          >
            <Save className="w-4 h-4 mr-1" />
            {viewingMonth && initialData?.month && viewingMonth !== initialData.month
              ? `Save to ${MONTHS[viewingMonth - 1]}`
              : "Save Plan"
            }
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Read-only inline viewer ─────────────────────────────────────────────────
export function PlanViewer({ tmpl, orgName, onClose, onEdit, onArchive, onRestore, onPrint, onSaveToMonth, saveToMonthLabel }) {
  const theme = THEMES[tmpl.templateType] || THEMES.toddler;
  const rows = tmpl.rows || DEFAULT_ROWS[tmpl.templateType] || [];
  const cells = tmpl.cells || {};
  const isArchived = tmpl.status === "archived";
  const weekDisplay = tmpl.weekOf
    ? new Date(tmpl.weekOf + "T00:00:00").toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })
    : "—";

  return (
    <div className={`rounded-2xl border-2 ${theme.border} ${theme.bg} overflow-hidden mt-2`}>
      {/* Header */}
      <div className={`${theme.header} text-white px-6 py-3 flex items-center gap-4`}>
        <img src="/simple-sign-in-logo.png" alt="Logo" className="h-8 w-8 rounded-lg bg-white p-0.5 object-contain flex-shrink-0" />
        <div className="flex-1 text-center">
          <h2 className="text-lg font-bold leading-tight">{orgName || "Childcare Center"}</h2>
          <p className="text-white/70 text-xs">{theme.label} Lesson Plan</p>
        </div>
        <div className="text-right flex-shrink-0">
          <p className="font-bold text-white text-sm">{tmpl.staffName}</p>
          <p className="text-white/70 text-xs">Teacher / Staff</p>
        </div>
      </div>

      {/* Meta bar */}
      <div className="px-6 py-2 bg-white/70 border-b border-gray-200 flex flex-wrap gap-6 text-sm text-gray-700">
        <span><span className="font-semibold text-gray-500 text-xs uppercase tracking-wide mr-1">Teacher:</span>{tmpl.staffName || "—"}</span>
        <span><span className="font-semibold text-gray-500 text-xs uppercase tracking-wide mr-1">Theme:</span>{tmpl.theme || "—"}</span>
        <span><span className="font-semibold text-gray-500 text-xs uppercase tracking-wide mr-1">Week of:</span>{weekDisplay}</span>
        {isArchived && <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full self-center">Archived</span>}
      </div>

      {/* Grid — read only */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[800px]">
          <thead>
            <tr>
              <th className="px-3 py-2 text-left text-xs font-bold uppercase tracking-wide text-gray-500 bg-white/80 w-56 border-b border-r border-gray-200">Activity / Time</th>
              {["Mon","Tue","Wed","Thu","Fri"].map(d => (
                <th key={d} className="px-3 py-2 text-center text-xs font-bold uppercase tracking-wide text-gray-600 bg-white/80 border-b border-r border-gray-200">{d}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => {
              const cell = cells[row.key] || {};
              return (
                <tr key={row.key} className={idx % 2 === 0 ? "bg-white/40" : "bg-white/20"}>
                  <td className="px-3 py-2 border-r border-b border-gray-200 text-xs font-semibold text-gray-700 align-top">{cell.label || row.label}</td>
                  {["monday","tuesday","wednesday","thursday","friday"].map(day => (
                    <td key={day} className="px-3 py-2 border-r border-b border-gray-200 text-xs text-gray-700 align-top whitespace-pre-wrap">{cell[day] || ""}</td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Action bar */}
      <div className="px-6 py-3 bg-white/60 border-t border-gray-200 flex items-center justify-between flex-wrap gap-2 print:hidden">
        <Button variant="outline" size="sm" onClick={onClose}>Close</Button>
        <div className="flex items-center gap-2">
          {isArchived ? (
            <Button variant="outline" size="sm" className="text-green-700 border-green-300 hover:bg-green-50" onClick={() => onRestore(tmpl.id)}>
              <RotateCcw className="w-4 h-4 mr-1" /> Restore
            </Button>
          ) : (
            <Button variant="outline" size="sm" onClick={() => onArchive(tmpl.id)}>
              <Archive className="w-4 h-4 mr-1" /> Archive
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => {
            try {
              const html = buildPrintHtml({
                orgName: orgName || "",
                type: tmpl.templateType,
                teacherName: tmpl.staffName || "",
                weekDisplay,
                themeText: tmpl.theme || "",
                rows,
                cells,
              });
              openPrintWindow(html);
            } catch (e) {
              if (onPrint) onPrint();
              else window.print();
            }
          }}>
            <Printer className="w-4 h-4 mr-1" /> Print
          </Button>
          <Button
            variant="outline"
            size="sm"
            data-testid="download-template-xlsx-btn"
            onClick={async () => {
              try {
                const tk = localStorage.getItem("token");
                const resp = await axios.get(
                  `${API}/lesson-plan-templates/${tmpl.id}/download`,
                  { responseType: "blob", headers: { Authorization: `Bearer ${tk}` } }
                );
                const cd = resp.headers["content-disposition"] || "";
                const mm = cd.match(/filename="?([^"]+)"?/);
                const fname = mm ? mm[1] : `${tmpl.theme || "lesson_plan"}.xlsx`;
                const url = window.URL.createObjectURL(resp.data);
                const a = document.createElement("a");
                a.href = url; a.download = fname;
                document.body.appendChild(a); a.click();
                window.URL.revokeObjectURL(url); a.remove();
                toast.success("Template downloaded");
              } catch (err) {
                toast.error(err?.response?.data?.detail || "Failed to download template");
              }
            }}
          >
            <Download className="w-4 h-4 mr-1" /> Download
          </Button>
          {saveToMonthLabel && onSaveToMonth && (
            <Button
              size="sm"
              onClick={onSaveToMonth}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="save-to-month-btn"
            >
              <Save className="w-4 h-4 mr-1" /> {saveToMonthLabel}
            </Button>
          )}
          {!isArchived && (
            <Button size="sm" onClick={() => onEdit(tmpl)} className={`${theme.header} text-white hover:opacity-90`}>
              Edit Plan
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Saved plan row card ─────────────────────────────────────────────────────
function SavedCard({ tmpl, isDirector, isExpanded, onToggleExpand, onEdit, onArchive, onRestore, onDelete }) {
  const theme = THEMES[tmpl.templateType] || THEMES.toddler;
  const isArchived = tmpl.status === "archived";
  return (
    <div className={`rounded-xl border-2 ${theme.border} ${theme.bg} px-4 py-3 flex items-center justify-between gap-3 ${isArchived ? "opacity-70" : ""}`}>
      <div className="flex items-center gap-3 min-w-0">
        <span className={`text-xs font-bold px-2 py-0.5 rounded-full flex-shrink-0 ${theme.badge}`}>{theme.label}</span>
        {isArchived && <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full">Archived</span>}
        <div className="min-w-0">
          <p className="font-semibold text-gray-800 text-sm truncate">
            {tmpl.theme || "No theme set"} — Week of {tmpl.weekOf || "—"}
          </p>
          <p className="text-xs text-gray-500">{tmpl.staffName} &middot; {new Date(tmpl.createdAt).toLocaleDateString()}</p>
        </div>
      </div>
      <div className="flex items-center gap-1 flex-shrink-0">
        <Button
          variant="ghost"
          size="sm"
          className="h-7 text-xs text-blue-600 hover:text-blue-800"
          onClick={onToggleExpand}
          data-testid={`view-template-${tmpl.id}`}
        >
          {isExpanded ? "Close" : "View"}
        </Button>
        {!isArchived && (
          <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => onEdit(tmpl)}>
            Edit
          </Button>
        )}
        {isArchived ? (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-green-600 hover:text-green-800"
            onClick={() => onRestore(tmpl.id)}
            title="Restore from archive"
            data-testid={`restore-template-${tmpl.id}`}
          >
            <RotateCcw className="w-4 h-4" />
          </Button>
        ) : (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-gray-400 hover:text-amber-600"
            onClick={() => onArchive(tmpl.id)}
            title="Archive"
            data-testid={`archive-template-${tmpl.id}`}
          >
            <Archive className="w-4 h-4" />
          </Button>
        )}
        {isDirector && (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-gray-400 hover:text-red-600"
            onClick={() => onDelete(tmpl.id)}
            title="Delete"
            data-testid={`delete-template-${tmpl.id}`}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        )}
      </div>
    </div>
  );
}

// ── Main exported component ─────────────────────────────────────────────────
export default function LessonPlanTemplates({ viewingMonth, staffFilterId, onView, startEditRef, onPrint }) {
  const { user, token } = useAuth();
  const [orgName, setOrgName] = useState("");
  const [savedTemplates, setSavedTemplates] = useState([]);
  const [editing, setEditing] = useState(null);       // { type, data } | null
  const [expandedIds, setExpandedIds] = useState(new Set()); // fallback inline expand (archived / no-parent)
  const [showArchived, setShowArchived] = useState(false);
  const [open, setOpen] = useState(true);

  const isDirector = user?.isOwner || user?.isPrimaryOwner || user?.isAdmin || user?.role === "owner" || user?.role === "co-owner";
  const authHeaders = { Authorization: `Bearer ${token}` };

  const activeTemplates = savedTemplates.filter(t => t.status !== "archived");
  const archivedTemplates = savedTemplates.filter(t => t.status === "archived");

  // Expose startEditing to parent via ref so it can trigger editing from the top viewer's Edit button
  useEffect(() => {
    if (startEditRef) {
      startEditRef.current = (tmpl) => {
        setEditing({ type: tmpl.templateType, data: tmpl });
      };
    }
  });

  useEffect(() => {
    axios.get(`${API}/organizations/contact-info`, { headers: authHeaders })
      .then(r => setOrgName(r.data?.name || ""))
      .catch(() => {});
  }, [token]);

  useEffect(() => {
    fetchTemplates();
  }, [viewingMonth, staffFilterId, token]);

  const fetchTemplates = async () => {
    try {
      const params = new URLSearchParams();
      if (viewingMonth) params.set("month", viewingMonth);
      if (staffFilterId) params.set("staff_id", staffFilterId);
      params.set("include_archived", "true");
      const res = await axios.get(`${API}/lesson-plan-templates?${params}`, { headers: authHeaders });
      setSavedTemplates(res.data.templates || []);
      return res.data.templates || [];
    } catch { return []; }
  };

  const toggleExpand = (id) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleViewCard = (tmpl) => {
    if (onView) {
      onView(tmpl); // send to parent top-card viewer
    } else {
      toggleExpand(tmpl.id); // fallback: inline
    }
  };

  const handleSave = async ({ weekOf, theme, cells, rows }) => {
    const month = viewingMonth || new Date().getMonth() + 1;
    const year = new Date().getFullYear();
    // If editing an existing plan but viewing a different month → copy to new month
    const isCrossMonthSave = editing?.data?.id && editing.data.month && editing.data.month !== month;
    try {
      let savedId;
      if (editing?.data?.id && !isCrossMonthSave) {
        await axios.put(`${API}/lesson-plan-templates/${editing.data.id}`, { weekOf, theme, cells, rows }, { headers: authHeaders });
        savedId = editing.data.id;
        toast.success("Plan updated");
      } else {
        const res = await axios.post(`${API}/lesson-plan-templates`, {
          templateType: editing.type, weekOf, theme, cells, rows, month, year
        }, { headers: authHeaders });
        savedId = res.data.template?.id;
        toast.success(isCrossMonthSave ? `Plan copied to ${MONTHS[month - 1]}` : "Plan saved");
      }
      setEditing(null);
      const updated = await fetchTemplates();
      if (savedId && onView) {
        const saved = updated.find(t => t.id === savedId);
        if (saved) onView(saved);
      } else if (savedId) {
        setExpandedIds(prev => new Set([...prev, savedId]));
      }
    } catch { toast.error("Failed to save plan"); }
  };

  const handleArchive = async (id) => {
    try {
      await axios.post(`${API}/lesson-plan-templates/${id}/archive`, {}, { headers: authHeaders });
      toast.success("Plan archived");
      setExpandedIds(prev => { const n = new Set(prev); n.delete(id); return n; });
      fetchTemplates();
    } catch { toast.error("Failed to archive"); }
  };

  const handleRestore = async (id) => {
    try {
      await axios.post(`${API}/lesson-plan-templates/${id}/restore`, {}, { headers: authHeaders });
      toast.success("Plan restored");
      fetchTemplates();
    } catch { toast.error("Failed to restore"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this lesson plan permanently?")) return;
    try {
      await axios.delete(`${API}/lesson-plan-templates/${id}`, { headers: authHeaders });
      toast.success("Deleted");
      setExpandedIds(prev => { const n = new Set(prev); n.delete(id); return n; });
      fetchTemplates();
    } catch { toast.error("Failed to delete"); }
  };

  const handlePrintViewer = () => {
    if (onPrint) {
      onPrint();
    } else {
      window.print();
    }
  };

  return (
    <div className="space-y-4">
      {/* Section header */}
      <div className="flex items-center justify-between print:hidden">
        <button
          onClick={() => setOpen(p => !p)}
          className="flex items-center gap-2 text-gray-700 font-semibold hover:text-blue-600 transition-colors"
        >
          {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          Lesson Plan Templates
        </button>
        <div className="flex items-center gap-2">
          <Button
            variant={showArchived ? "outline" : "ghost"}
            size="sm"
            className="text-xs"
            onClick={() => setShowArchived(p => !p)}
          >
            {showArchived ? "Hide Archived" : `Show Archived${archivedTemplates.length > 0 ? ` (${archivedTemplates.length})` : ""}`}
          </Button>
        </div>
      </div>

      {open && (
        <div className="space-y-4">
          {/* New plan type buttons — always shown unless editing */}
          {!editing && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 print:hidden">
              {Object.entries(THEMES).map(([type, th]) => (
                <button
                  key={type}
                  onClick={() => setEditing({ type, data: null })}
                  className={`rounded-xl border-2 ${th.border} ${th.bg} px-4 py-5 text-center hover:shadow-md transition-all group`}
                  data-testid={`new-template-${type}`}
                >
                  <Plus className="w-5 h-5 mx-auto mb-1 text-gray-400 group-hover:scale-110 transition-transform" />
                  <p className={`font-bold text-sm ${th.badge.split(" ")[1]}`}>{th.label}</p>
                  <p className="text-xs text-gray-400 mt-0.5">New lesson plan</p>
                </button>
              ))}
            </div>
          )}

          {/* Editable grid */}
          {editing && (
            <TemplateGrid
              type={editing.type}
              orgName={orgName}
              initialData={editing.data}
              onSave={handleSave}
              onCancel={() => setEditing(null)}
              onPrint={onPrint}
              viewingMonth={viewingMonth}
            />
          )}

          {/* Active saved plans — card list */}
          {activeTemplates.length > 0 && (
            <div className="space-y-2 print:hidden">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                Active Plans — {activeTemplates.length} plan{activeTemplates.length !== 1 ? "s" : ""}
              </p>
              {activeTemplates.map(tmpl => (
                <SavedCard
                  key={tmpl.id}
                  tmpl={tmpl}
                  isDirector={isDirector}
                  isExpanded={false}
                  onToggleExpand={() => handleViewCard(tmpl)}
                  onEdit={(t) => setEditing({ type: t.templateType, data: t })}
                  onArchive={handleArchive}
                  onRestore={handleRestore}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          )}

          {/* Archived plans — shown when toggled, with inline expand */}
          {showArchived && archivedTemplates.length > 0 && (
            <div className="space-y-2 print:hidden">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
                Archived Plans — {archivedTemplates.length}
              </p>
              {archivedTemplates.map(tmpl => (
                <div key={tmpl.id}>
                  <SavedCard
                    tmpl={tmpl}
                    isDirector={isDirector}
                    isExpanded={expandedIds.has(tmpl.id)}
                    onToggleExpand={() => toggleExpand(tmpl.id)}
                    onEdit={(t) => setEditing({ type: t.templateType, data: t })}
                    onArchive={handleArchive}
                    onRestore={handleRestore}
                    onDelete={handleDelete}
                  />
                  {expandedIds.has(tmpl.id) && (
                    <PlanViewer
                      tmpl={tmpl}
                      orgName={orgName}
                      onClose={() => toggleExpand(tmpl.id)}
                      onEdit={(t) => { toggleExpand(t.id); setEditing({ type: t.templateType, data: t }); }}
                      onArchive={handleArchive}
                      onRestore={handleRestore}
                      onPrint={handlePrintViewer}
                    />
                  )}
                </div>
              ))}
            </div>
          )}

          {savedTemplates.length === 0 && !editing && (
            <p className="text-sm text-gray-400 text-center py-4 print:hidden">No lesson plans yet. Click a type above to create one.</p>
          )}
        </div>
      )}
    </div>
  );
}
